import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, Shield, Network, Cloud, Phone, Headphones, CheckCircle } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { services } from '@/data/services'

export function SolutionsPage() {
  // Reorder services to show security-first approach
  const orderedServices = [
    services.find(s => s.id === 'cybersecurity'),
    services.find(s => s.id === 'network'),
    services.find(s => s.id === 'sd-wan'),
    services.find(s => s.id === 'ucaas'),
    services.find(s => s.id === 'ccaas'),
  ].filter(Boolean)

  return (
    <>
      <SEO
        title="Solutions"
        description="UC Advisors provides holistic technology optimization starting with cybersecurity, then network, SD-WAN, UCaaS, and CCaaS solutions."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge variant="glow" className="mb-6">Our Holistic Approach</Badge>
            
            <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
              Technology Optimization{' '}
              <span className="text-gradient">Starting with Security</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-8">
              We don't sell isolated products—we optimize your entire technology stack. 
              Every engagement starts with a security foundation, then builds up through 
              network, communications, and customer experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Integration Flow */}
      <section className="py-16 bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-display-sm font-heading font-bold text-white mb-4">
              The UC Advisors Methodology
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Security is the foundation. Everything else builds on top.
            </p>
          </motion.div>

          {/* Visual flow diagram */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-2">
            {[
              { icon: Shield, label: 'Security Foundation', step: 1, color: 'bg-accent-500' },
              { icon: Network, label: 'Network Infrastructure', step: 2, color: 'bg-accent-600' },
              { icon: Cloud, label: 'SD-WAN', step: 3, color: 'bg-electric-500' },
              { icon: Phone, label: 'UCaaS', step: 4, color: 'bg-electric-600' },
              { icon: Headphones, label: 'CCaaS', step: 5, color: 'bg-accent-700' },
            ].map((item, index, arr) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center"
              >
                <div className="glass-card p-6 text-center min-w-[140px]">
                  <div className={`w-12 h-12 mx-auto rounded-xl ${item.color} flex items-center justify-center mb-3`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xs font-mono text-accent-400 mb-1">Step {item.step}</div>
                  <div className="text-sm font-medium text-white">{item.label}</div>
                </div>
                {index < arr.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-accent-500 mx-2 hidden lg:block" />
                )}
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center text-slate-500 mt-8 text-sm"
          >
            Each layer depends on the one below. You can't have reliable communications without a secure network.
          </motion.p>
        </div>
      </section>

      {/* All Solutions */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Complete Technology Stack</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Our Solution Areas
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Provider-agnostic recommendations backed by data, not vendor quotas.
            </p>
          </motion.div>

          <div className="space-y-8">
            {orderedServices.map((service, index) => {
              if (!service) return null
              const ServiceIcon = service.icon
              return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={service.href}>
                  <div className={`glass-card-hover p-8 rounded-2xl ${index === 0 ? 'ring-2 ring-accent-500/30' : ''}`}>
                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                      <div className="flex-shrink-0">
                        <div className={`w-16 h-16 rounded-2xl ${index === 0 ? 'bg-gradient-to-br from-accent-500 to-accent-600 shadow-glow' : 'bg-gradient-to-br from-accent-500/20 to-electric-500/20'} flex items-center justify-center`}>
                          <ServiceIcon className={`w-8 h-8 ${index === 0 ? 'text-white' : 'text-accent-400'}`} />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-heading font-semibold text-white">
                            {service.shortTitle}
                          </h3>
                          {index === 0 && (
                            <Badge variant="glow" className="text-2xs">Foundation</Badge>
                          )}
                          {service.certifications && (
                            <Badge variant="secondary" className="text-2xs">Zoom Certified</Badge>
                          )}
                        </div>
                        <p className="text-slate-400 mb-4">{service.description}</p>
                        <div className="flex flex-wrap gap-4">
                          {service.benefits.slice(0, 3).map((benefit) => (
                            <div key={benefit} className="flex items-center gap-2 text-sm text-slate-300">
                              <CheckCircle className="w-4 h-4 text-accent-500" />
                              {benefit}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex-shrink-0 self-center">
                        <ArrowRight className="w-6 h-6 text-accent-400" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Why Holistic Approach */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4">Why It Matters</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Isolated Solutions Create Isolated Problems
              </h2>
              <p className="text-lg text-slate-400 mb-6">
                Most technology vendors sell you a single product. When something goes wrong, 
                you're left pointing fingers between vendors. Our holistic approach means:
              </p>
              <div className="space-y-4">
                {[
                  'Single point of accountability across your entire stack',
                  'Security considerations baked into every recommendation',
                  'Interoperability tested before deployment',
                  'Unified support and escalation path',
                  'Cost optimization across all technology spend',
                ].map((point, index) => (
                  <motion.div
                    key={point}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-accent-500" />
                    </div>
                    <span className="text-slate-300">{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                Start with a Security Assessment
              </h3>
              <p className="text-slate-400 mb-6">
                Every engagement begins with understanding your current security posture. 
                From there, we map your network, communications, and operational needs 
                to build a complete optimization roadmap.
              </p>
              <Link to="/contact">
                <Button variant="glow" className="w-full group">
                  Request Security Assessment
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}

